// Offline ticket verification (spec §6, §8.3; feature 008 contract
// `ticket-verification.md`).
//
// @noble/ed25519 rather than WebCrypto (research R1): WebCrypto's Ed25519
// support varies by browser build, so using it means a feature detect plus a
// fallback — two code paths through the one operation that decides whether a
// delegate gets into the venue. Noble takes the raw 32-byte key directly and
// behaves the same on every device.
//
// The API's own verifyTicket is node:crypto and cannot be reused here. The wire
// format is the contract between them, not the code.
import { verifyAsync } from '@noble/ed25519';

export const VERDICT = {
  ADMIT: 'ADMIT',
  ADMIT_UNKNOWN: 'ADMIT_UNKNOWN',
  DUPLICATE: 'DUPLICATE',
  REJECT: 'REJECT',
  WRONG_EVENT: 'WRONG_EVENT',
  UNREADABLE: 'UNREADABLE',
};

function base64urlToBytes(s) {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Verify a scanned QR string against the event's public key.
 *
 * Returns the decoded payload, or a failure reason. Never throws: a camera
 * pointed at the world decodes all sorts of things, and none of them may take
 * the scanner down.
 *
 * Order matters and is not negotiable:
 *   1. parse the envelope
 *   2. verify the signature
 *   3. ONLY THEN interpret the payload
 * An unverified payload is attacker-controlled input; JSON.parse never runs on
 * bytes that have not already proven authentic.
 */
export async function verifyTicketQr(qr, publicKeyBase64url) {
  if (typeof qr !== 'string' || qr.length === 0) {
    return { ok: false, reason: VERDICT.UNREADABLE };
  }

  const dot = qr.indexOf('.');
  if (dot < 0) {
    // Not a Kontriticket ticket at all — a URL, a wifi code, a payment QR.
    // Distinct from a ticket that failed verification, because the volunteer's
    // next action differs: "that is not a ticket" vs "that ticket is not real".
    return { ok: false, reason: VERDICT.UNREADABLE };
  }

  let body;
  let signature;
  try {
    body = base64urlToBytes(qr.slice(0, dot));
    signature = base64urlToBytes(qr.slice(dot + 1));
  } catch {
    return { ok: false, reason: VERDICT.UNREADABLE };
  }

  let valid = false;
  try {
    valid = await verifyAsync(signature, body, base64urlToBytes(publicKeyBase64url));
  } catch {
    // Malformed key or signature length — noble throws rather than returning
    // false. A failure to verify is a failure to verify.
    valid = false;
  }
  if (!valid) return { ok: false, reason: VERDICT.REJECT };

  let payload;
  try {
    payload = JSON.parse(new TextDecoder().decode(body));
  } catch {
    // Signed by us, but not shaped like a ticket. Should be impossible; if it
    // ever happens it is not an admission.
    return { ok: false, reason: VERDICT.UNREADABLE };
  }

  if (!payload || typeof payload.registrationId !== 'string') {
    return { ok: false, reason: VERDICT.UNREADABLE };
  }

  return { ok: true, payload };
}
